import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../utils/cn'

const badgeVariants = cva(
  'badge inline-flex w-fit items-center whitespace-nowrap rounded-full font-medium uppercase tracking-wide',
  {
    variants: {
      variant: {
        outline: 'border border-line-subtle bg-transparent text-content-primary',
        solid: 'border border-line-subtle bg-surface-subtle text-content-primary',
        subtle: 'bg-surface-subtle text-content-secondary',
      },
      size: {
        sm: 'px-2 py-0.5 text-[0.6875rem]',
        md: 'px-3 py-0.5 text-xs',
        lg: 'px-4 py-1 text-sm',
      },
    },
    defaultVariants: { variant: 'outline', size: 'sm' },
  },
)

// Indigo is the design-language secondary accent; it has no theme-aware CSS
// token (the brand scale is fixed), so dark-mode variants are spelled out.
const indigoBadge =
  'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/15 dark:text-indigo-200'

// The secondary brand accent (charts, eyebrows, pills). Like the indigo scale it
// is fixed, so dark-mode variants are spelled out.
const brandAccentBadge =
  'border-brand-accent-200 bg-brand-accent-50 text-brand-accent-700 dark:border-brand-accent-500/30 dark:bg-brand-accent-500/15 dark:text-brand-accent-200'

const colorSchemeMap: Record<string, string> = {
  green: 'border-success-border bg-success-bg text-success',
  success: 'border-success-border bg-success-bg text-success',
  red: 'border-danger-border bg-danger-bg text-danger',
  danger: 'border-danger-border bg-danger-bg text-danger',
  orange: 'border-warning-border bg-warning-bg text-warning',
  yellow: 'border-warning-border bg-warning-bg text-warning',
  warning: 'border-warning-border bg-warning-bg text-warning',
  // Informational / brand-secondary statuses (processing, admin, info, …).
  accent: indigoBadge,
  blue: indigoBadge,
  indigo: indigoBadge,
  purple: indigoBadge,
  // Secondary brand accent pill.
  brand: brandAccentBadge,
  // Neutral-but-tinted statuses.
  gray: 'border-info-border bg-info-bg text-info',
  info: 'border-info-border bg-info-bg text-info',
}

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  colorScheme?: string
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, colorScheme, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        badgeVariants({ variant, size }),
        colorScheme && colorSchemeMap[colorScheme],
        className,
      )}
      {...props}
    />
  ),
)
Badge.displayName = 'Badge'

/** Tag alias (square-ish badge). */
export const Tag = React.forwardRef<HTMLSpanElement, BadgeProps>(({ className, ...props }, ref) => (
  <Badge
    ref={ref}
    className={cn(
      'inline-flex items-center gap-1 rounded-md normal-case tracking-normal',
      className,
    )}
    {...props}
  />
))
Tag.displayName = 'Tag'

export const TagLabel = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & { isTruncated?: boolean }
>(({ className, isTruncated, ...props }, ref) => (
  <span ref={ref} className={cn(isTruncated && 'truncate', className)} {...props} />
))
TagLabel.displayName = 'TagLabel'

export const TagCloseButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label="Remove"
    className={cn(
      'ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-content-muted transition-colors hover:bg-surface-muted hover:text-content-primary',
      className,
    )}
    {...props}
  >
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  </button>
))
TagCloseButton.displayName = 'TagCloseButton'

export const TagLeftIcon = ({
  as: As,
  className,
}: {
  as: React.ElementType
  className?: string
}) => <As className={cn('mr-1', className)} />
export const TagRightIcon = ({
  as: As,
  className,
}: {
  as: React.ElementType
  className?: string
}) => <As className={cn('ml-1', className)} />
